<script setup lang="ts">
import { productIncludes } from '~/utils/includes/product'

const props = defineProps<{
    navigationId: string;
}>();

const { search } = useProductSearch()

const { data: productResponse, error } = await useAsyncData(
    'cmsProduct' + props.navigationId,
    async () => {
        return await search(props.navigationId, {
            withCmsAssociations: true,
            criteria: {
                includes: {
                    ...productIncludes,
                    // Backend-built breadcrumb path (respects Dynamic Access) used below. Only surfaces
                    // when the extension is explicitly listed on product; the breadcrumb key trims each
                    // entry to the fields the breadcrumb needs.
                    product: [...productIncludes.product!, 'a4g_breadcrumb'],
                    breadcrumb: ['name', 'path', 'categoryId'],
                },
                customFieldsMedia: {
                    greenfeed_link_image: {}
                },
            },
            associations: {
                crossSellings: {},
                deliveryTime: {},
                media: {
                    associations: {
                        media: {}
                    }
                },
                cover: {
                    associations: {
                        media: {}
                    }
                },
                manufacturer: {
                    associations: {
                        media: {}
                    }
                },
                seoUrls: {},
                canonicalProduct: {},
            }
        })
    },
)

// HULL/parent products with a canonical variant carry a seo_redirect extension on the
// response (A4G-3168): 301 to the canonical variant's SEO URL. Must run before the 404
// check, since the parent product itself may still be present in the response.
const seoRedirect = productResponse.value?.extensions?.seo_redirect
if (seoRedirect != null) {
    const { redirectTarget, redirectCode, externalTarget } = mapSeoRedirect(seoRedirect)
    // Shopware's seoPathInfo has no notion of our locale-prefix routing — format it through
    // the current locale like any other internal link, or the redirect drops the active
    // language (and can 404 server-side, since the slug only resolves under that language).
    const target = externalTarget
        ? redirectTarget
        : useInternationalization(useLocalePath()).formatLink(redirectTarget)
    await navigateTo(target, {
        redirectCode,
        external: externalTarget,
    })
}

if (productResponse.value?.product == null && seoRedirect == null) {
    console.error('[FrontendDetailPage.vue] product not found', error.value?.message)
    // Shopware sends 410 (instead of 404) for discontinued products that were
    // tracked at deletion time (ProductDetailRouteDecorator). Preserve that status instead
    // of collapsing every "product missing" case to 404, so crawlers see the real Gone state.
    const isGone = error.value?.statusCode === 410
    throw createError({
        statusCode: isGone ? 410 : 404,
        statusMessage: isGone ? 'Gone' : 'Page Not Found',
        fatal: true
    })
}
</script>

<template>
    <!-- `navigateTo` above does not abort `<script setup>`, so this component keeps rendering
         while the 301 is in flight - and on a HULL/parent redirect the response may carry no
         product at all. Withholding the content component is what keeps its product-dependent
         setup from running: a component that is not rendered never runs setup. -->
    <FrontendDetailPageContent
        v-if="seoRedirect == null"
        :product-response="productResponse!"
    />
</template>
